"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { signIn } from "next-auth/react";
import { useTranslations } from "@i18n/provider";
import { ConvoIcon } from "@ui/ConvoIcon";
import { PRIVACY_URL, TERMS_URL, hasLegalDocuments } from "@lib/legal";
import {
  SIGN_IN_INVITATIONS,
  SIGN_IN_INVITATION_MS,
  nextSignInInvitation,
} from "@lib/i18n/signInInvitations";
import { prefersReducedMotion } from "@lib/motion/reducedMotion";
import { GoogleMark } from "./GoogleMark";
import { AppleMark } from "./AppleMark";

type Mode = "signin" | "create" | "reset";

export default function LoginForm({
  devBypassEnabled,
  googleEnabled,
  appleEnabled = false,
  signInError = null,
  invitation = 0,
}: {
  devBypassEnabled: boolean;
  googleEnabled: boolean;
  appleEnabled?: boolean;
  signInError?: string | null;
  invitation?: number;
}) {
  const { t, locale } = useTranslations();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(signInError ? t("login.providerError") : null);
  const [invitationIndex, setInvitationIndex] = useState(invitation);

  const emailOpen = showEmail || mode === "reset" || codeSent;
  const emailInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showEmail) emailInput.current?.focus();
  }, [showEmail]);

  useEffect(() => {
    if (mode !== "signin" || prefersReducedMotion()) return;
    const timer = setInterval(
      () => setInvitationIndex((index) => nextSignInInvitation(index)),
      SIGN_IN_INVITATION_MS,
    );
    return () => clearInterval(timer);
  }, [mode]);

  function changeMode(next: Mode) {
    setMode(next);
    setCodeSent(false);
    setCode("");
    setPassword("");
    setError(null);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (mode !== "signin") {
        const response = await fetch("/api/auth/password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: codeSent ? "complete" : "request",
            purpose: mode,
            email: email.trim(),
            locale,
            ...(codeSent ? { code, password } : {}),
          }),
        });
        if (!response.ok) throw new Error("verification");
        if (!codeSent) {
          setCodeSent(true);
          return;
        }
      }
      const result = await signIn("password", {
        email: email.trim(),
        password,
        redirect: false,
        callbackUrl: "/",
      });
      if (result?.error || !result?.ok) throw new Error("credentials");
      window.location.assign(new URL("/", window.location.origin).href);
    } catch {
      setError(
        t(mode === "signin" ? "login.passwordFlow.signInError" : "login.passwordFlow.verifyError"),
      );
    } finally {
      setBusy(false);
    }
  }

  async function providerSignIn(provider: "google" | "apple" | "dev-bypass") {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await signIn(provider, { callbackUrl: "/" });
    } catch {
      setError(t("login.providerError"));
      setBusy(false);
    }
  }

  const title =
    mode === "create"
      ? t("login.passwordFlow.createTitle")
      : mode === "reset"
        ? t("login.passwordFlow.resetTitle")
        : t("app.title");
  const subtitle =
    mode === "signin" ? t(SIGN_IN_INVITATIONS[invitationIndex]) : t("login.passwordFlow.subtitle");
  const submitLabel = busy
    ? t("login.passwordFlow.working")
    : codeSent
      ? t("login.passwordFlow.verifyContinue")
      : mode === "signin"
        ? t("login.passwordFlow.signIn")
        : mode === "create"
          ? t("login.passwordFlow.createAccount")
          : t("login.passwordFlow.sendCode");
  const providerClass =
    "flex min-h-12 w-full items-center justify-center gap-3 rounded-full border border-surface-border px-4 text-sm font-semibold disabled:opacity-50";

  return (
    <main
      className="flex min-h-[100svh] w-full flex-col items-center justify-center px-5 pb-10 pt-5 sm:pb-14"
      data-testid="login-centered"
    >
      <section
        className="surface-card w-full max-w-md p-6 sm:px-9 sm:py-7"
        aria-labelledby="login-title"
      >
        <div className="mb-5 text-center">
          <ConvoIcon className="mx-auto h-12 w-12" />
          <h1 id="login-title" className="font-display mt-3 text-2xl font-bold text-ink">
            {title}
          </h1>
          <p
            key={subtitle}
            data-testid="login-subtitle"
            className="generation-progress-message mt-2 text-sm text-ink-muted"
          >
            {subtitle}
          </p>
        </div>
        {mode !== "reset" && !codeSent ? (
          <div className="mb-4 flex flex-col gap-3">
            <button
              type="button"
              disabled={busy || !appleEnabled}
              onClick={() => void providerSignIn("apple")}
              className={`${providerClass} bg-black text-white`}
            >
              <AppleMark />
              {t("login.passwordFlow.apple")}
            </button>
            <button
              type="button"
              disabled={busy || !googleEnabled}
              onClick={() => void providerSignIn("google")}
              className={`${providerClass} bg-white text-[#1f1f1f]`}
            >
              <GoogleMark />
              {t("login.signInGoogle")}
            </button>
            <div className="flex items-center gap-3 text-xs text-ink-faint">
              <span className="flex-1 border-t border-surface-border" />
              {t("login.orDivider")}
              <span className="flex-1 border-t border-surface-border" />
            </div>
            {!emailOpen ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => setShowEmail(true)}
                className={`${providerClass} bg-surface text-ink`}
              >
                {t("login.passwordFlow.continueWithEmail")}
              </button>
            ) : null}
            {!emailOpen && error ? (
              <p role="alert" className="text-center text-sm text-writing-fg">
                {error}
              </p>
            ) : null}
          </div>
        ) : null}
        {emailOpen ? (
          <div className="login-email-expand">
            <div className="-m-1 min-h-0 overflow-hidden p-1">
              {mode !== "reset" && !codeSent ? (
                <div
                  className="mb-4 grid grid-cols-2 gap-1 rounded-full bg-coral-tint-soft p-1"
                  role="group"
                  aria-label={t("login.passwordFlow.accountOptions")}
                >
                  {(["signin", "create"] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      disabled={busy}
                      aria-pressed={mode === option}
                      onClick={() => changeMode(option)}
                      className={`rounded-full px-3 py-2.5 text-sm font-semibold ${mode === option ? "bg-surface text-coral-fg shadow-sm" : "text-ink-muted"}`}
                    >
                      {t(
                        option === "signin"
                          ? "login.passwordFlow.signIn"
                          : "login.passwordFlow.createAccount",
                      )}
                    </button>
                  ))}
                </div>
              ) : null}
              <form onSubmit={submit} className="flex flex-col gap-3">
                <label className="flex flex-col gap-1.5 text-sm text-ink-body">
                  {t("login.emailLabel")}
                  <input
                    ref={emailInput}
                    id="login-email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("login.emailPlaceholder")}
                    required
                    autoComplete="email"
                    readOnly={codeSent}
                    className="inset-field"
                  />
                </label>
                <label className="flex flex-col gap-1.5 text-sm text-ink-body">
                  {t(
                    mode === "signin"
                      ? "login.passwordFlow.password"
                      : "login.passwordFlow.newPassword",
                  )}
                  <input
                    id="login-password"
                    type="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={mode === "signin" ? 1 : 12}
                    maxLength={1024}
                    autoComplete={mode === "signin" ? "current-password" : "new-password"}
                    className="inset-field"
                  />
                </label>
                {mode !== "signin" ? (
                  <p className="text-xs text-ink-muted">{t("login.passwordFlow.passwordHint")}</p>
                ) : null}
                {codeSent ? (
                  <label className="flex flex-col gap-2 text-sm text-ink-body">
                    {t("login.passwordFlow.codeSent")}
                    <input
                      id="login-code"
                      aria-label={t("login.passwordFlow.codeLabel")}
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      required
                      className="inset-field text-center tracking-[0.4em]"
                      autoFocus
                    />
                  </label>
                ) : null}
                {error ? (
                  <p role="alert" className="text-sm text-writing-fg">
                    {error}
                  </p>
                ) : null}
                <button type="submit" className="btn-primary w-full" disabled={busy}>
                  {submitLabel}
                </button>
                <button
                  type="button"
                  className="min-h-10 text-sm text-coral-fg underline underline-offset-4"
                  disabled={busy}
                  onClick={() => changeMode(mode === "signin" ? "reset" : "signin")}
                >
                  {t(
                    mode === "signin"
                      ? "login.passwordFlow.resetPassword"
                      : "login.passwordFlow.backToSignIn",
                  )}
                </button>
              </form>
            </div>
          </div>
        ) : null}
        {devBypassEnabled ? (
          <button
            type="button"
            data-testid="login-dev-bypass"
            disabled={busy}
            onClick={() => void providerSignIn("dev-bypass")}
            className="mt-5 w-full text-xs text-ink-muted"
          >
            {t("login.devBypass")}
          </button>
        ) : null}
      </section>
      {hasLegalDocuments() ? (
        <p
          className="mt-5 flex flex-wrap justify-center gap-4 text-xs text-ink-muted"
          data-testid="login-legal-links"
        >
          <a data-testid="login-terms-link" href={TERMS_URL} className="underline">
            {t("billing.legal.terms")}
          </a>
          <a data-testid="login-privacy-link" href={PRIVACY_URL} className="underline">
            {t("billing.legal.privacy")}
          </a>
        </p>
      ) : null}
      <a href="https://convergic.io" className="mt-3 text-xs text-ink-faint">
        {t("login.madeBy")}
      </a>
    </main>
  );
}
